/**
 * The public homepage. No Figma frame yet, so it sits in the public frame
 * (Getting in's top bar and footer) with style B shapes: capsule buttons from
 * the kit, the features as raised r16 cards on the ground.
 */
import { Link } from "react-router";
import { ArrowRight } from "lucide-react";
import { isAuthenticated } from "@/lib/auth";
import { useMetadata } from "@/hooks/useMetadata";
import { cn } from "@/utils/helpers";
import { kitButtonClass } from "@/app/routes/dashboard/components/kit/Button";
import { PublicFrame } from "@/components/gettingIn/PublicFrame";

const features = [
  {
    title: "Brand Universe",
    body: "Capture your brand DNA once — voice, visuals, audience, and guardrails — and let every output inherit it.",
  },
  {
    title: "Strategy",
    body: "Turn your brand universe into positioning, pillars, and campaign strategy with agents that reason over your context.",
  },
  {
    title: "Content Calendar",
    body: "Plan, brief, and schedule production-ready content across channels, including TikTok, from one calendar.",
  },
  {
    title: "Market Signals & Trends",
    body: "Track trends and market signals in your category and convert them into on-brand content opportunities.",
  },
];

export default function LandingPage() {
  const authed = isAuthenticated();

  useMetadata({
    title: "Mohtawa Creative OS",
    description:
      "Mohtawa Creative OS is the AI-native creative operating system for brands. Capture your brand DNA once and run strategy, concepting, and production from one intelligent source of truth.",
  });

  return (
    <PublicFrame>
      <section className="mx-auto w-full max-w-[880px] px-6 pt-20 pb-16 text-center lg:pt-28">
        <p className="text-[13px] font-medium leading-[18px] text-co-muted">
          AI-native creative operating system
        </p>
        <h1 className="mt-4 font-display text-[44px] leading-[1.05] tracking-[-0.02em] text-balance text-co-text sm:text-[64px]">
          Mohtawa Creative OS
        </h1>
        <p className="mx-auto mt-5 max-w-[560px] text-[16px] leading-6 text-pretty text-co-text2">
          Capture your brand DNA once and run strategy, concepting, and
          production from one intelligent source of truth — built for brands
          across the UAE and KSA.
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Link
            to={authed ? "/dashboard" : "/signup"}
            className={cn(
              "unstyled-link",
              kitButtonClass({ variant: "primary", size: "lg" }),
              "h-12 gap-2.5 px-[22px] text-[15px] font-semibold",
            )}
          >
            {authed ? "Open dashboard" : "Get started"}
            <ArrowRight aria-hidden className="size-4" />
          </Link>
          {!authed && (
            <Link
              to="/login"
              className={cn(
                "unstyled-link",
                kitButtonClass({ variant: "secondary", size: "lg" }),
                "h-12 px-[22px] text-[15px] font-semibold",
              )}
            >
              Sign in
            </Link>
          )}
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1080px] px-6 pb-24 max-sm:px-4">
        <div className="grid gap-3 sm:grid-cols-2 sm:gap-4">
          {features.map((f) => (
            <article key={f.title} className="rounded-panel bg-co-surface p-7 max-sm:p-6">
              <h2 className="text-[17px] font-semibold leading-6 text-co-text">{f.title}</h2>
              <p className="mt-2 text-[14px] leading-[22px] text-co-muted">{f.body}</p>
            </article>
          ))}
        </div>
      </section>
    </PublicFrame>
  );
}
